import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Fragment, type ReactNode } from 'react';
import { Icon } from './Icon';

export interface PageHeaderProps {
  breadcrumbs?: string[];
  /** Omitted when the page renders its own heading (e.g. the student record header). */
  title?: string;
  subtitle?: ReactNode;
  /** Rendered next to the title (e.g. a StatusChip). */
  status?: ReactNode;
  actions?: ReactNode;
}

/** Breadcrumbs (13px, chevrons mirrored in RTL), 28/36 title, subtitle and right-aligned actions. */
export function PageHeader({ breadcrumbs = [], title, subtitle, status, actions }: PageHeaderProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 3,
        flexWrap: 'wrap',
      }}
    >
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        {breadcrumbs.length > 0 && (
          <Box
            component="nav"
            aria-label="breadcrumb"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 13, color: 'text.tertiary' }}
          >
            {breadcrumbs.map((c, i) => (
              <Fragment key={`${c}-${i}`}>
                {i > 0 && <Icon name="chevron_right" size={16} mirrorInRtl />}
                <Box
                  component="span"
                  sx={i === breadcrumbs.length - 1 ? { color: 'text.primary' } : undefined}
                >
                  {c}
                </Box>
              </Fragment>
            ))}
          </Box>
        )}
        {title && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h3" component="h1">
              {title}
            </Typography>
            {status}
          </Box>
        )}
        {subtitle && <Typography sx={{ color: 'text.secondary' }}>{subtitle}</Typography>}
      </Stack>
      {actions && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>{actions}</Box>
      )}
    </Box>
  );
}
